import { useState, useEffect, useContext, useRef } from "react";
import { C, API, post } from "./utils";
import { ThemeCtx } from "./AppShell";
import CommentsPanel from "./CommentsPanel";

function fmtDateRange(c) {
  if (!c) return "";
  const { STARTDATE, STARTMONTH, STARTYEAR, ENDDATE, ENDMONTH, ENDYEAR } = c;
  if (!STARTDATE) return "";
  if (STARTMONTH === ENDMONTH && STARTYEAR === ENDYEAR)
    return `${STARTDATE}–${ENDDATE} ${STARTMONTH} ${STARTYEAR}`;
  return `${STARTDATE} ${STARTMONTH} – ${ENDDATE} ${ENDMONTH} ${STARTYEAR}`;
}

const openDocTab = (doc, subId) => {
  if (!doc?.DOCUMENTID) return;
  const base = window.location.origin + window.location.pathname;
  window.open(`${base}?doc=${doc.DOCUMENTID}&dt=${doc.DOCTYPEID}&sub=${subId||""}`, "_blank", "noopener,noreferrer");
};

// Distinct colors per session type
const TYPE_COLORS = {
  "Industry Sponsored Event":    { bg:"#E1F5EE", text:"#065F46", dot:"#059669" },
  "Oral Abstract Session":       { bg:"#E6F1FB", text:"#0C447C", dot:"#2563EB" },
  "Poster Session":              { bg:"#EEEDFE", text:"#3C3489", dot:"#7C3AED" },
  "Poster Sessions":             { bg:"#EEEDFE", text:"#3C3489", dot:"#7C3AED" },
  "Special Session":             { bg:"#FEF3C7", text:"#92400E", dot:"#D97706" },
  "Education Session":           { bg:"#FEE2E2", text:"#7F1D1D", dot:"#EF4444" },
  "Rapid Oral Abstract Session": { bg:"#FCE7F3", text:"#831843", dot:"#EC4899" },
  "Clinical Science Symposium":  { bg:"#DBEAFE", text:"#1E3A5F", dot:"#3B82F6" },
  "Industry Expert Theater":     { bg:"#FFF7ED", text:"#9A3412", dot:"#EA580C" },
  "Industry Satellite Symposium":{ bg:"#F0FDF4", text:"#14532D", dot:"#22C55E" },
  "Keynote":                     { bg:"#FFEDD5", text:"#7C2D12", dot:"#F97316" },
};
const getTC = (t) => TYPE_COLORS[t] || { bg:"#F3F4F6", text:"#374151", dot:"#6B7280" };

const toggleBookmark = async (user, activeSub, congress, opts, isBookmarked) => {
  const fd = new FormData();
  fd.append("userid", user.USERID);
  if(isBookmarked) {
    // DELETE
    if(opts.presentationid) fd.append("presentationid", opts.presentationid);
    if(opts.documentid)     fd.append("documentid",     opts.documentid);
    try {
      const r   = await fetch(`${API}/delete_bookmark`,
        {method:"POST", headers:{"Auth-Code":user.AUTH_CODE}, body:fd});
      const res = await r.json();
      return res.STATUS==="1"||res.STATUS===1;
    } catch(e) { return false; }
  } else {
    // INSERT
    fd.append("congressid",   congress.CONGRESSID);
    fd.append("subprojectid", activeSub.SUBPROJECTID);
    if(opts.presentationid) fd.append("presentationid", opts.presentationid);
    if(opts.documentid)     fd.append("documentid",     opts.documentid);
    try {
      const r   = await fetch(`${API}/insert_bookmarks`,
        {method:"POST", headers:{"Auth-Code":user.AUTH_CODE}, body:fd});
      const res = await r.json();
      return res.STATUS==="1"||res.STATUS===1;
    } catch(e) { return false; }
  }
};

// ─── Draggable date filter ────────────────────────────────────────────────────
function DateFilterRow({ dates, active, onSelect, color }) {
  const ref      = useRef(null);
  const dragging = useRef(false);
  const startX   = useRef(0);
  const scrollL  = useRef(0);
  const onMD=e=>{dragging.current=true;startX.current=e.pageX-ref.current.offsetLeft;scrollL.current=ref.current.scrollLeft;ref.current.style.cursor="grabbing";};
  const onML=()=>{dragging.current=false;if(ref.current)ref.current.style.cursor="grab";};
  const onMU=()=>{dragging.current=false;if(ref.current)ref.current.style.cursor="grab";};
  const onMM=e=>{if(!dragging.current)return;e.preventDefault();const x=e.pageX-ref.current.offsetLeft;ref.current.scrollLeft=scrollL.current-(x-startX.current)*1.2;};
  const onW=e=>{e.preventDefault();ref.current.scrollLeft+=e.deltaY;};
  return(
    <div ref={ref} onMouseDown={onMD} onMouseLeave={onML} onMouseUp={onMU}
      onMouseMove={onMM} onWheel={onW}
      style={{flexShrink:0,display:"flex",gap:"6px",flexWrap:"nowrap",overflowX:"auto",
        padding:"7px 14px",background:"#fff",borderBottom:"1px solid #E5E7EB",
        scrollbarWidth:"none",msOverflowStyle:"none",alignItems:"center",
        cursor:"grab",userSelect:"none"}}>
      <span style={{fontSize:"11px",color:"#9CA3AF",fontWeight:"600",flexShrink:0,
        cursor:"default",pointerEvents:"none"}}>Date:</span>
      {[{id:"all",l:"All dates"},...dates.map(d=>({id:d,l:d}))].map(d=>(
        <button key={d.id} onClick={()=>onSelect(d.id)}
          style={{padding:"3px 10px",borderRadius:"20px",flexShrink:0,
            border:`1px solid ${active===d.id?color:"#E5E7EB"}`,cursor:"pointer",
            fontSize:"11px",fontWeight:"500",
            background:active===d.id?color:"#fff",
            color:active===d.id?"#fff":"#6B7280",
            fontFamily:"inherit",transition:"all .15s",whiteSpace:"nowrap"}}>
          {d.l}
        </button>
      ))}
    </div>
  );
}

// ─── Draggable session type filter ───────────────────────────────────────────
function TypeFilterRow({ types, active, onSelect, color }) {
  const scrollRef  = useRef(null);
  const isDragging = useRef(false);
  const startX     = useRef(0);
  const scrollLeft = useRef(0);

  const onMouseDown = e => {
    isDragging.current = true;
    startX.current     = e.pageX - scrollRef.current.offsetLeft;
    scrollLeft.current = scrollRef.current.scrollLeft;
    scrollRef.current.style.cursor = "grabbing";
  };
  const onMouseLeave = () => { isDragging.current=false; if(scrollRef.current) scrollRef.current.style.cursor="grab"; };
  const onMouseUp    = () => { isDragging.current=false; if(scrollRef.current) scrollRef.current.style.cursor="grab"; };
  const onMouseMove  = e => {
    if(!isDragging.current) return;
    e.preventDefault();
    const x    = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.2;
    scrollRef.current.scrollLeft = scrollLeft.current - walk;
  };
  const onWheel = e => {
    e.preventDefault();
    scrollRef.current.scrollLeft += e.deltaY;
  };

  return (
    <div ref={scrollRef}
      onMouseDown={onMouseDown} onMouseLeave={onMouseLeave}
      onMouseUp={onMouseUp}    onMouseMove={onMouseMove}
      onWheel={onWheel}
      style={{flexShrink:0,display:"flex",gap:"6px",flexWrap:"nowrap",
        overflowX:"auto",padding:"7px 14px",background:"#fff",
        borderBottom:"1px solid #E5E7EB",scrollbarWidth:"none",
        msOverflowStyle:"none",alignItems:"center",
        cursor:"grab",userSelect:"none",WebkitOverflowScrolling:"touch"}}>
      <span style={{fontSize:"11px",color:"#9CA3AF",fontWeight:"600",
        flexShrink:0,cursor:"default",pointerEvents:"none"}}>Type:</span>
      <button onClick={()=>onSelect("all")}
        style={{padding:"3px 10px",borderRadius:"20px",flexShrink:0,
          border:`1px solid ${active==="all"?color:"#E5E7EB"}`,
          cursor:"pointer",fontSize:"11px",fontWeight:"500",
          background:active==="all"?color:"#fff",
          color:active==="all"?"#fff":"#6B7280",
          fontFamily:"inherit",whiteSpace:"nowrap"}}>All</button>
      {types.map(t=>{
        const tc=getTC(t);
        const isAct=active===t;
        return(
          <button key={t} onClick={()=>onSelect(isAct?"all":t)}
            style={{padding:"3px 10px",borderRadius:"20px",flexShrink:0,
              border:`1px solid ${isAct?tc.dot:"#E5E7EB"}`,
              cursor:"pointer",fontSize:"11px",fontWeight:"500",
              background:isAct?tc.bg:"#fff",
              color:isAct?tc.text:"#6B7280",
              fontFamily:"inherit",transition:"background .12s,border .12s",
              whiteSpace:"nowrap"}}>
            <span style={{display:"inline-block",width:"7px",height:"7px",
              borderRadius:"50%",background:tc.dot,
              marginRight:"5px",verticalAlign:"middle",pointerEvents:"none"}}/>
            {t}
          </button>
        );
      })}
    </div>
  );
}

// ─── Right panel: Uploads + Reports ──────────────────────────────────────────
function CongressDocs({ congress, user, activeSub }) {
  const ctx   = useContext(ThemeCtx)||{};
  const color = ctx.theme?.primary||C.primary;
  const [tab,       setTab]       = useState("uploads");
  const [uploads,   setUploads]   = useState([]);
  const [reports,   setReports]   = useState([]);
  const [loadingU,  setLoadingU]  = useState(true);
  const [loadingR,  setLoadingR]  = useState(true);

  useEffect(()=>{
    if(!congress?.CONGRESSID||!user?.USERID||!activeSub?.SUBPROJECTID) return;
    const cid  = congress.CONGRESSID;
    const sub  = activeSub.SUBPROJECTID;
    const uid  = user.USERID;
    const auth = user.AUTH_CODE;
    // Uploads
    post("advance_filter",{subprojectid:sub,userid:uid,congressid:cid,first:0,last:50,sort:"recent"},auth)
      .then(res=>{
        const data=(Array.isArray(res.DATA)?res.DATA:[]).filter(d=>String(d.DOCTYPEID)!=="92");
        setUploads(data);
        setLoadingU(false);
        // If no uploads, switch to reports
        if(data.length===0) setTab("reports");
      })
      .catch(()=>setLoadingU(false));
    // Reports
    post("advance_filter",{subprojectid:sub,userid:uid,congressid:cid,first:0,last:50,sort:"recent",doctypeid:92},auth)
      .then(res=>{ setReports(Array.isArray(res.DATA)?res.DATA:[]); setLoadingR(false); })
      .catch(()=>setLoadingR(false));
  },[congress?.CONGRESSID]);

  const DocItem = ({ doc }) => {
    const title = doc.SUBJECT||doc.TITLE||"Untitled";
    const brand = doc.BRANDNAME&&doc.BRANDID!=="0" ? doc.BRANDNAME : null;
    const company = doc.SPONSORNAME&&doc.SPONSORID!=="0" ? doc.SPONSORNAME : null;
    const sub = [brand, company].filter(Boolean).join(" · ") || doc.DOCTYPENAME || "";
    return (
      <div onClick={()=>openDocTab(doc, activeSub?.SUBPROJECTID)}
        onMouseEnter={e=>e.currentTarget.style.background="#F8FAFC"}
        onMouseLeave={e=>e.currentTarget.style.background="#fff"}
        style={{ display:"flex", alignItems:"stretch", gap:0,
          borderBottom:"1px solid #F3F4F6", cursor:"pointer",
          background:"#fff", transition:"background .1s" }}>
        {/* Color accent bar */}
        <div style={{ width:"3px", flexShrink:0, background:color, borderRadius:"0" }}/>
        {/* Content */}
        <div style={{ flex:1, padding:"10px 12px", minWidth:0 }}>
          <div style={{ fontSize:"12px", fontWeight:"600", color:"#111",
            lineHeight:1.4, marginBottom:"3px",
            overflow:"hidden", textOverflow:"ellipsis",
            display:"-webkit-box", WebkitLineClamp:2,
            WebkitBoxOrient:"vertical" }}>{title}</div>
          {sub&&<div style={{ fontSize:"11px", color:"#6B7280",
            overflow:"hidden", textOverflow:"ellipsis",
            whiteSpace:"nowrap" }}>{sub}</div>}
          {doc.TIMESTAMP&&<div style={{ fontSize:"10px",
            color:"#9CA3AF", marginTop:"3px" }}>{doc.TIMESTAMP}</div>}
        </div>
      </div>
    );
  };

  const loading = tab==="uploads"?loadingU:loadingR;
  const items   = tab==="uploads"?uploads:reports;

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%",
      background:"#fff", borderLeft:"1px solid #E5E7EB" }}>
      {/* Tabs */}
      <div style={{ flexShrink:0, display:"flex",
        borderBottom:"1px solid #E5E7EB", background:"#fff" }}>
        {[
          {id:"uploads", label:"Uploads", count:uploads.length, loading:loadingU},
          {id:"reports", label:"Reports", count:reports.length, loading:loadingR},
        ].map(t=>{
          const isActive = tab===t.id;
          const hasData  = !t.loading && t.count>0;
          return (
            <button key={t.id} onClick={()=>setTab(t.id)}
              style={{ flex:1, padding:"10px 6px", border:"none", cursor:"pointer",
                background:"none", fontFamily:"inherit", fontSize:"12px",
                fontWeight:isActive?"600":"400",
                color:isActive?color:hasData?"#374151":"#9CA3AF",
                borderBottom:`2px solid ${isActive?color:"transparent"}`,
                display:"flex", alignItems:"center", justifyContent:"center",
                gap:"5px", transition:"all .15s" }}>
              {t.label}
              {hasData&&(
                <span style={{ fontSize:"10px", fontWeight:"700",
                  background:isActive?color:`${color}18`,
                  color:isActive?"#fff":color,
                  padding:"1px 6px", borderRadius:"20px",
                  lineHeight:1.4 }}>{t.count}</span>
              )}
            </button>
          );
        })}
      </div>
      {/* List */}
      <div style={{ flex:1, overflowY:"auto" }}>
        {loading&&<div style={{ padding:"30px", textAlign:"center",
          color:"#9CA3AF", fontSize:"12px" }}>Loading…</div>}
        {!loading&&items.length===0&&(
          <div style={{ padding:"40px 20px", textAlign:"center",
            color:"#9CA3AF", fontSize:"12px" }}>
            No {tab} found
          </div>
        )}
        {items.map(doc=><DocItem key={doc.DOCUMENTID} doc={doc}/>)}
      </div>
    </div>
  );
}

// ─── Bookmark button ─────────────────────────────────────────────────────────
function BookmarkBtn({ bookmarked, onToggle, color, style={}, size=16 }) {
  const [spinning, setSpinning] = useState(false);
  const handle = async(e) => {
    e.stopPropagation();
    if(spinning) return;
    setSpinning(true);
    await onToggle();
    setSpinning(false);
  };
  return (
    <button onClick={handle} title={bookmarked?"Remove bookmark":"Bookmark"}
      style={{background:"none",border:"none",cursor:"pointer",
        fontSize:size,lineHeight:1,padding:"4px",flexShrink:0,
        opacity:spinning?0.5:1,transition:"opacity .15s",
        minWidth:"32px",minHeight:"32px",
        display:"flex",alignItems:"center",justifyContent:"center",
        ...style}}>
      {bookmarked
        ? "🔖"
        : <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
            stroke={color} strokeWidth="2.5">
            <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/>
          </svg>
      }
    </button>
  );
}

// ─── Congress Notes ───────────────────────────────────────────────────────────
function CongressNotes({ user, congress, sub }) {
  const ctx   = useContext(ThemeCtx)||{};
  const color = ctx.theme?.primary||C.primary;
  const [notes,    setNotes]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [sending,  setSending]  = useState(false);
  const [text,     setText]     = useState("");
  const bottomRef = useRef(null);

  const presId  = String(sub?.PRESENTATIONID||"");
  const congId  = String(congress?.CONGRESSID||"");

  // Load notes
  useEffect(()=>{
    if(!presId||!congId||!user?.USERID) return;
    setLoading(true);
    fetch(`${API}/view_cong_notes?congressid=${congId}&presentationid=${presId}&userid=${user.USERID}`,
      {headers:{"Auth-Code":user.AUTH_CODE}})
      .then(r=>r.json())
      .then(res=>{
        const data = Array.isArray(res.DATA)?res.DATA:[];
        setNotes(data.sort((a,b)=>(a.UNIX_TIME||0)-(b.UNIX_TIME||0)));
        setLoading(false);
      })
      .catch(()=>setLoading(false));
  },[presId, congId]);

  // Scroll to bottom on new notes
  useEffect(()=>{
    bottomRef.current?.scrollIntoView({behavior:"smooth"});
  },[notes]);

  const send = async () => {
    if(!text.trim()||sending) return;
    setSending(true);
    const fd = new FormData();
    fd.append("presentationid", presId);
    fd.append("congressid",     congId);
    fd.append("userid",         user.USERID);
    fd.append("notes",          text.trim());
    try{
      const r   = await fetch(`${API}/insert_cong_notes`,
        {method:"POST", headers:{"Auth-Code":user.AUTH_CODE}, body:fd});
      const res = await r.json();
      if((res.STATUS==="1"||res.STATUS===1)&&res.DATA){
        const note = Array.isArray(res.DATA)?res.DATA[0]:res.DATA;
        setNotes(prev=>[...prev, note]);
        setText("");
      }
    }catch(e){}
    setSending(false);
  };

  const onKey = e => { if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();} };

  const initials = (first,last) =>
    ((first||"").charAt(0)+(last||"").charAt(0)).toUpperCase()||"?";

  const isMe = n => String(n.USERID)===String(user.USERID);

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden",
      background:"#F8FAFC"}}>

      {/* Notes list */}
      <div style={{flex:1,overflowY:"auto",padding:"16px"}}>
        {loading&&(
          <div style={{textAlign:"center",padding:"40px",
            color:"#9CA3AF",fontSize:"13px"}}>Loading notes…</div>
        )}
        {!loading&&notes.length===0&&(
          <div style={{textAlign:"center",padding:"60px 20px"}}>
            <div style={{fontSize:"32px",marginBottom:"10px"}}>📝</div>
            <div style={{fontSize:"14px",fontWeight:"600",
              color:"#374151",marginBottom:"4px"}}>No notes yet</div>
            <div style={{fontSize:"12px",color:"#9CA3AF",lineHeight:1.6}}>
              Add your private notes for this presentation below.
            </div>
          </div>
        )}
        {notes.map(n=>{
          const me   = isMe(n);
          const name = `${n.FIRSTNAME||""} ${n.LASTNAME||""}`.trim();
          return (
            <div key={n.NOTEID}
              style={{display:"flex",gap:"8px",marginBottom:"12px",
                flexDirection:me?"row-reverse":"row",alignItems:"flex-end"}}>
              {/* Avatar */}
              {!me&&(
                <div style={{width:"28px",height:"28px",borderRadius:"50%",
                  background:`${color}22`,color,fontSize:"11px",fontWeight:"700",
                  display:"flex",alignItems:"center",justifyContent:"center",
                  flexShrink:0}}>
                  {initials(n.FIRSTNAME,n.LASTNAME)}
                </div>
              )}
              <div style={{maxWidth:"75%"}}>
                {/* Name + time */}
                {!me&&name&&(
                  <div style={{fontSize:"10px",color:"#9CA3AF",
                    marginBottom:"3px",paddingLeft:"4px"}}>{name}</div>
                )}
                {/* Bubble */}
                <div style={{
                  background:me?color:"#fff",
                  color:me?"#fff":"#111",
                  padding:"10px 13px",
                  borderRadius:me?"16px 16px 4px 16px":"16px 16px 16px 4px",
                  fontSize:"13px",lineHeight:1.5,
                  border:me?"none":"1px solid #E5E7EB",
                  boxShadow:"0 1px 3px rgba(0,0,0,0.06)",
                  wordBreak:"break-word",
                  whiteSpace:"pre-wrap",
                }}>
                  {n.NOTES}
                </div>
                {/* Timestamp */}
                <div style={{fontSize:"10px",color:"#9CA3AF",
                  marginTop:"3px",textAlign:me?"right":"left",
                  paddingLeft:me?"0":"4px",paddingRight:me?"4px":"0"}}>
                  {n.TIMESTAMP}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef}/>
      </div>

      {/* Input */}
      <div style={{flexShrink:0,padding:"10px 14px",background:"#fff",
        borderTop:"1px solid #E5E7EB",
        display:"flex",gap:"8px",alignItems:"flex-end"}}>
        <textarea value={text} onChange={e=>setText(e.target.value)}
          onKeyDown={onKey}
          placeholder="Write a note…"
          rows={1}
          style={{flex:1,border:"1px solid #E5E7EB",borderRadius:"20px",
            padding:"8px 14px",fontSize:"13px",outline:"none",resize:"none",
            fontFamily:"inherit",lineHeight:1.5,maxHeight:"80px",
            overflowY:"auto",background:"#F8FAFC",color:"#111"}}/>
        <button onClick={send} disabled={!text.trim()||sending}
          style={{width:"36px",height:"36px",borderRadius:"50%",border:"none",
            cursor:text.trim()?"pointer":"default",flexShrink:0,
            background:text.trim()?color:"#E5E7EB",
            display:"flex",alignItems:"center",justifyContent:"center",
            transition:"background .15s"}}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke={text.trim()?"#fff":"#9CA3AF"} strokeWidth="2.5">
            <line x1="22" y1="2" x2="11" y2="13"/>
            <polygon points="22 2 15 22 11 13 2 9 22 2"/>
          </svg>
        </button>
      </div>
    </div>
  );
}

// ─── Presentation Detail ──────────────────────────────────────────────────────
function PresentationDetail({ sub, congress, user, activeSub, color, onBack, initCommentId }) {
  const [tab,        setTab]        = useState("comments");
  const [bookmarked, setBookmarked] = useState(sub.BOOKMARKED===1||sub.BOOKMARKED==="1");

  const docId  = String(sub.PARENT_DOCUMENTID||sub.DOCUMENTID||"");
  const presId = String(sub.PRESENTATIONID||"");
  const congId = String(congress?.CONGRESSID||"");
  const extraChatParams = { presentationid: presId, congressid: congId };

  const Pill = ({ value, id, bg, text }) => {
    if (!value||value==="0"||value===0||value===null) return null;
    if (id!==undefined && (id==="0"||id===0||id===null)) return null;
    return (
      <span style={{ display:"inline-flex", alignItems:"center",
        padding:"3px 10px", borderRadius:"20px", background:bg,
        border:`1px solid ${text}22`, fontSize:"12px", fontWeight:"500",
        color:text, marginRight:"6px", marginBottom:"6px" }}>{value}</span>
    );
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden" }}>
      {/* Green hero */}
      <div style={{ flexShrink:0, background:color, color:"#fff", padding:"12px 20px 10px" }}>
        <button onClick={onBack}
          style={{ display:"inline-flex", alignItems:"center", gap:"5px",
            fontSize:"12px", opacity:.8, background:"none", border:"none",
            cursor:"pointer", color:"#fff", fontFamily:"inherit",
            padding:0, marginBottom:"8px" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to agenda
        </button>
        {sub.PARENTTITLE&&(
          <div style={{ fontSize:"12px", opacity:.8, marginBottom:"4px",
            lineHeight:1.35 }}>{sub.PARENTTITLE}</div>
        )}
        <div style={{ display:"flex", gap:"8px", flexWrap:"wrap", alignItems:"center" }}>
          {sub.SESSIONTYPENAME&&(
            <span style={{ fontSize:"11px", fontWeight:"600",
              background:"rgba(255,255,255,.2)", padding:"2px 9px",
              borderRadius:"20px" }}>{sub.SESSIONTYPENAME}</span>
          )}
          {sub.STARTTIME&&(
            <span style={{ fontSize:"12px", opacity:.9 }}>
              {sub.STARTTIME}{sub.ENDTIME?` – ${sub.ENDTIME}`:""}
            </span>
          )}
          {sub.LOCATIONNAME&&(
            <span style={{ fontSize:"12px", opacity:.75 }}>📍 {sub.LOCATIONNAME}</span>
          )}
          <BookmarkBtn
            bookmarked={bookmarked}
            onToggle={async()=>{
              const ok=await toggleBookmark(user,activeSub,congress,{presentationid:presId},bookmarked);
              if(ok!==false) setBookmarked(b=>!b);
              return ok;
            }}
            color="#fff"
            style={{marginLeft:"auto"}}/>
        </div>
      </div>

      {/* White detail area */}
      <div style={{ flexShrink:0, background:"#fff",
        borderBottom:"1px solid #E5E7EB", padding:"14px 20px 12px" }}>
        {sub.SUBSESSIONTITLE&&(
          <div style={{ fontSize:"15px", fontWeight:"600", color:"#111",
            lineHeight:1.4, marginBottom:"10px" }}>
            {sub.SUBSESSIONTITLE}
          </div>
        )}
        <div style={{ display:"flex", flexWrap:"wrap", marginTop:"4px" }}>
          <Pill value={sub.SPONSORNAME}    id={sub.SPONSORID}    bg="#FAF5FF" text="#6D28D9"/>
          <Pill value={sub.BRANDNAME}      id={sub.BRANDID}      bg="#EFF6FF" text="#1D4ED8"/>
          <Pill value={sub.INDICATIONNAME} id={sub.INDICATIONID} bg="#F0FDF4" text="#15803D"/>
          <Pill value={sub.MOANAME}                              bg="#FFF7ED" text="#C2410C"/>
        </div>
        {sub.AUTHORNAME&&(
          <div style={{ fontSize:"12px", color:"#6B7280", marginTop:"4px" }}>
            👤 {sub.AUTHORNAME}
          </div>
        )}
        {(!!sub.COMMENTCOUNT||!!sub.IMAGECOUNT||!!sub.VIDEOCOUNT)&&(
          <div style={{ display:"flex", gap:"12px", marginTop:"6px" }}>
            {!!sub.COMMENTCOUNT&&<span style={{fontSize:"11px",color:"#9CA3AF"}}>💬 {sub.COMMENTCOUNT}</span>}
            {!!sub.IMAGECOUNT&&  <span style={{fontSize:"11px",color:"#9CA3AF"}}>🖼 {sub.IMAGECOUNT}</span>}
            {!!sub.VIDEOCOUNT&&  <span style={{fontSize:"11px",color:"#9CA3AF"}}>🎥 {sub.VIDEOCOUNT}</span>}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ flexShrink:0, display:"flex",
        borderBottom:"1px solid #E5E7EB", background:"#fff" }}>
        {[{id:"comments",l:"Comments"},{id:"notes",l:"Notes"}].map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{ flex:1, padding:"10px 6px", border:"none", cursor:"pointer",
              background:"none", fontFamily:"inherit", fontSize:"13px",
              fontWeight:tab===t.id?"600":"400",
              color:tab===t.id?color:"#6B7280",
              borderBottom:`2px solid ${tab===t.id?color:"transparent"}`,
              transition:"all .15s" }}>{t.l}</button>
        ))}
      </div>

      {/* Tab body */}
      <div style={{ flex:1, overflow:"hidden" }}>
        {tab==="comments"&&(
          <CommentsPanel user={user} activeSub={activeSub}
            documentId={docId} extraParams={extraChatParams} pollOnly={false}
            scrollToComment={initCommentId||null}/>
        )}
        {tab==="notes"&&(
          <CongressNotes user={user} congress={congress} sub={sub}/>
        )}
      </div>
    </div>
  );
}

// ─── Sub row ──────────────────────────────────────────────────────────────────
function SubRow({ sub, color, isLast, onSelect, user, activeSub, congress, onBookmarkToggle }) {
  const [hov, setHov] = useState(false);
  const isBookmarked = sub.BOOKMARKED===1||sub.BOOKMARKED==="1";
  return (
    <div onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      onClick={()=>onSelect(sub)}
      style={{ display:"flex", alignItems:"stretch",
        borderBottom: isLast?"none":"1px solid #F3F4F6",
        background: hov?`${color}06`:"#FAFAFA",
        cursor:"pointer", transition:"background .1s" }}>
      {/* Left indent bar */}
      <div style={{ width:"68px", flexShrink:0, display:"flex",
        justifyContent:"center", paddingTop:"12px" }}>
        <div style={{ width:"2px", height:"calc(100% - 8px)",
          background:`${color}33`, borderRadius:"2px" }}/>
      </div>
      {/* Content */}
      <div style={{ flex:1, minWidth:0, padding:"10px 6px 10px 0" }}>
        <div style={{ fontSize:"12px", fontWeight:"500", color:"#111",
          lineHeight:1.4, marginBottom:"4px" }}>{sub.SUBSESSIONTITLE}</div>
        <div style={{ display:"flex", gap:"8px", flexWrap:"wrap", alignItems:"center" }}>
          {(sub.STARTTIME||sub.ENDTIME)&&(
            <span style={{ fontSize:"11px", color:"#6B7280", fontWeight:"500" }}>
              {sub.STARTTIME||""}{sub.ENDTIME?` – ${sub.ENDTIME}`:""}
            </span>
          )}
          {sub.AUTHORNAME&&(
            <span style={{ fontSize:"11px", color:"#9CA3AF",
              overflow:"hidden", textOverflow:"ellipsis",
              whiteSpace:"nowrap", maxWidth:"160px" }}>
              {sub.AUTHORNAME}
            </span>
          )}
          {sub.BRANDNAME&&(
            <span style={{ fontSize:"10px", fontWeight:"600", color,
              background:`${color}12`, padding:"1px 7px",
              borderRadius:"6px" }}>{sub.BRANDNAME}</span>
          )}
          {sub.INDICATIONNAME&&(
            <span style={{ fontSize:"10px", color:"#15803D",
              background:"#F0FDF4", padding:"1px 6px",
              borderRadius:"6px" }}>{sub.INDICATIONNAME}</span>
          )}
          {!!sub.COMMENTCOUNT&&(
            <span style={{ fontSize:"10px", color:"#9CA3AF" }}>💬 {sub.COMMENTCOUNT}</span>
          )}
        </div>
      </div>
      {/* Bookmark — right edge, large tap area */}
      <div style={{ flexShrink:0, display:"flex", alignItems:"center",
        padding:"0 10px" }} onClick={e=>e.stopPropagation()}>
        <BookmarkBtn
          bookmarked={isBookmarked}
          onToggle={async()=>{
            const ok=await toggleBookmark(user,activeSub,congress,{presentationid:sub.PRESENTATIONID},isBookmarked);
            if(ok!==false) onBookmarkToggle&&onBookmarkToggle(sub.PRESENTATIONID);
            return ok;
          }}
          color={color} size={20}/>
      </div>
      {/* Chevron */}
      <div style={{ flexShrink:0, padding:"12px 10px 12px 0",
        display:"flex", alignItems:"center" }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
          stroke={hov?color:"#D1D5DB"} strokeWidth="2.5">
          <path d="M9 18l6-6-6-6"/>
        </svg>
      </div>
    </div>
  );
}

// ─── Session tile ─────────────────────────────────────────────────────────────
function SessionTile({ session: s, color, expanded, onToggle, onSelect, user, activeSub, congress, onBookmarkToggle }) {
  const [hov, setHov] = useState(false);
  const hasSubs    = s.SUBSESSIONS?.length > 0;
  const tc         = getTC(s.SESSIONTYPENAME);
  const isHot      = s.HOTTOPIC===1||s.HOTTOPIC==="1";
  const isBookmark = s.BOOKMARKED===1||s.BOOKMARKED==="1";

  return (
    <div onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{ background:"#fff", borderRadius:"10px", overflow:"hidden",
        marginBottom:"8px", transition:"all .15s",
        borderTop:    `1px solid ${expanded?color+"66":hov?color+"44":"#E5E7EB"}`,
        borderRight:  `1px solid ${expanded?color+"66":hov?color+"44":"#E5E7EB"}`,
        borderBottom: `1px solid ${expanded?color+"66":hov?color+"44":"#E5E7EB"}`,
        borderLeft:   `4px solid ${tc.dot}`,
        boxShadow: hov||expanded?"0 3px 12px rgba(0,0,0,0.08)":"0 1px 3px rgba(0,0,0,0.04)" }}>

      <div style={{ display:"flex", alignItems:"stretch", cursor:"pointer" }}
        onClick={()=>hasSubs?onToggle():null}>

        {/* Time column */}
        <div style={{ flexShrink:0, width:"68px", display:"flex",
          flexDirection:"column", alignItems:"center", justifyContent:"center",
          padding:"12px 4px", borderRight:"1px solid #F3F4F6",
          background: expanded?`${color}06`:"#FAFAFA" }}>
          {s.STARTTIME?(
            <>
              <span style={{ fontSize:"12px", fontWeight:"600",
                color, lineHeight:1.2 }}>{s.STARTTIME}</span>
              {s.ENDTIME&&(
                <>
                  <div style={{ width:"1px", height:"10px",
                    background:`${color}33`, margin:"3px 0" }}/>
                  <span style={{ fontSize:"12px", fontWeight:"600",
                    color, lineHeight:1.2 }}>{s.ENDTIME}</span>
                </>
              )}
            </>
          ):<span style={{ fontSize:"18px" }}>📋</span>}
        </div>

        {/* Content */}
        <div style={{ flex:1, minWidth:0, padding:"11px 12px" }}>
          <div style={{ display:"flex", gap:"6px", flexWrap:"wrap",
            alignItems:"center", marginBottom:"6px" }}>
            {s.SESSIONTYPENAME&&(
              <span style={{ fontSize:"10px", fontWeight:"600",
                background:tc.bg, color:tc.text,
                padding:"2px 8px", borderRadius:"20px",
                flexShrink:0 }}>{s.SESSIONTYPENAME}</span>
            )}
            {s.LOCATIONNAME&&(
              <span style={{ fontSize:"11px", color:"#6B7280",
                display:"flex", alignItems:"center", gap:"3px" }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                {s.LOCATIONNAME}
              </span>
            )}
            <div style={{ marginLeft:"auto", display:"flex", gap:"4px",
              alignItems:"center" }}>
              {isHot&&(
                <span style={{ fontSize:"10px", background:"#FEF2F2",
                  color:"#B91C1C", padding:"1px 7px", borderRadius:"20px",
                  fontWeight:"600" }}>🔥 Hot</span>
              )}
            </div>
          </div>
          <div style={{ fontSize:"13px", fontWeight:"500", color:"#111",
            lineHeight:1.4, marginBottom:"5px" }}>{s.TITLE}</div>
          {s.BRANDNAME&&(
            <div style={{ marginBottom:"4px" }}>
              <span style={{ fontSize:"11px", fontWeight:"500", color,
                background:`${color}10`, padding:"1px 7px",
                borderRadius:"6px" }}>{s.BRANDNAME}</span>
              {s.INDICATIONNAME&&(
                <span style={{ fontSize:"11px", color:"#15803D",
                  background:"#F0FDF4", padding:"1px 7px",
                  borderRadius:"6px", marginLeft:"5px" }}>{s.INDICATIONNAME}</span>
              )}
            </div>
          )}
          <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
            {!!s.COMMENTCOUNT&&<span style={{fontSize:"10px",color:"#9CA3AF"}}>💬 {s.COMMENTCOUNT}</span>}
            {!!s.IMAGECOUNT&&  <span style={{fontSize:"10px",color:"#9CA3AF"}}>🖼 {s.IMAGECOUNT}</span>}
            {!!s.VIDEOCOUNT&&  <span style={{fontSize:"10px",color:"#9CA3AF"}}>🎥 {s.VIDEOCOUNT}</span>}
            {hasSubs&&(
              <span style={{ fontSize:"11px", color:"#6B7280",
                marginLeft:"auto", fontWeight:"500" }}>
                {s.SUBSESSIONS.length} presentation{s.SUBSESSIONS.length!==1?"s":""}
              </span>
            )}
          </div>
        </div>

        {hasSubs&&(
          <div style={{ flexShrink:0, display:"flex", alignItems:"center",
            padding:"0 6px", background:expanded?`${color}06`:"transparent" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke={expanded?color:"#9CA3AF"} strokeWidth="2.5"
              style={{ transition:"transform .2s",
                transform:expanded?"rotate(180deg)":"none" }}>
              <path d="M6 9l6 6 6-6"/>
            </svg>
          </div>
        )}
        {/* Bookmark — always at far right, stops propagation */}
        <div style={{ flexShrink:0, display:"flex", alignItems:"center",
          padding:"0 10px 0 4px" }} onClick={e=>e.stopPropagation()}>
          <BookmarkBtn
            bookmarked={isBookmark}
            onToggle={async()=>{
              const ok=await toggleBookmark(user,activeSub,congress,{documentid:s.DOCUMENTID},isBookmark);
              if(ok!==false) onBookmarkToggle&&onBookmarkToggle(s.DOCUMENTID);
              return ok;
            }}
            color={color} size={20}/>
        </div>
      </div>

      {expanded&&hasSubs&&(
        <div style={{ borderTop:`1px solid ${color}22` }}>
          {s.SUBSESSIONS.map((sub,i)=>(
            <SubRow key={sub.PRESENTATIONID||i}
              sub={{...sub, PARENTTITLE:s.TITLE, PARENT_DOCUMENTID:s.DOCUMENTID}}
              color={color} isLast={i===s.SUBSESSIONS.length-1}
              onSelect={onSelect}
              user={user} activeSub={activeSub} congress={congress}
              onBookmarkToggle={pid=>onBookmarkToggle&&onBookmarkToggle(pid)}/>
          ))}
        </div>
      )}
    </div>
  );
}


const inputStyle = {
  width:"100%", padding:"9px 12px", borderRadius:"8px", fontSize:"13px",
  border:"1px solid #E5E7EB", outline:"none", fontFamily:"inherit",
  color:"#111", background:"#fff", boxSizing:"border-box",
};

const Field = ({label, required, children}) => (
  <div style={{marginBottom:"14px"}}>
    <label style={{display:"block",fontSize:"11px",fontWeight:"600",
      color:"#6B7280",textTransform:"uppercase",letterSpacing:".4px",
      marginBottom:"5px"}}>
      {label}{required&&<span style={{color:"#EF4444",marginLeft:"2px"}}>*</span>}
    </label>
    {children}
  </div>
);

// ─── Check-in Attendees Panel (Admin) ────────────────────────────────────────
function AttendeePanel({ user, activeSub, congress, onClose }) {
  const ctx   = useContext(ThemeCtx)||{};
  const color = ctx.theme?.primary||C.primary;
  const [attendees, setAttendees] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [acting,    setActing]    = useState({}); // {userid: true} while API in flight

  const load = () => {
    setLoading(true);
    fetch(`${API}/congress_attendees?userid=${user.USERID}&subprojectid=${activeSub.SUBPROJECTID}&congressid=${congress.CONGRESSID}`,
      {headers:{"Auth-Code":user.AUTH_CODE}})
      .then(r=>r.json())
      .then(res=>{ setAttendees(Array.isArray(res.DATA)?res.DATA:[]); setLoading(false); })
      .catch(()=>setLoading(false));
  };

  useEffect(()=>{ load(); },[congress?.CONGRESSID]);

  const doAction = async(uid, action) => {
    setActing(a=>({...a,[uid]:true}));
    const fd=new FormData();
    fd.append("userid",    uid);
    fd.append("congressid",congress.CONGRESSID);
    fd.append("action",    action);
    try{
      const r=await fetch(`${API}/check_in_action`,
        {method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const res=await r.json();
      if(res.STATUS==="1"||res.STATUS===1){
        if(action===2){
          // Delete — remove from list
          setAttendees(prev=>prev.filter(a=>String(a.USERID)!==String(uid)));
        } else {
          // Approve/deactivate — update status
          setAttendees(prev=>prev.map(a=>
            String(a.USERID)===String(uid)?{...a,STATUS:action===1?1:0}:a
          ));
        }
      }
    }catch(e){}
    setActing(a=>({...a,[uid]:false}));
  };

  const StatusBadge = ({status}) => {
    if(status===1) return <span style={{fontSize:"10px",fontWeight:"600",
      color:"#065F46",background:"#D1FAE5",padding:"2px 8px",borderRadius:"20px"}}>
      ✓ Approved</span>;
    if(status===0) return <span style={{fontSize:"10px",fontWeight:"600",
      color:"#92400E",background:"#FEF3C7",padding:"2px 8px",borderRadius:"20px"}}>
      ⏳ Pending</span>;
    return null;
  };

  return (
    <div style={{position:"fixed",top:"48px",left:"200px",right:0,bottom:0,
      zIndex:960,display:"flex",flexDirection:"column",background:"#fff"}}>
      {/* Header */}
      <div style={{flexShrink:0,background:color,color:"#fff",
        padding:"14px 20px",display:"flex",alignItems:"center",gap:"12px"}}>
        <button onClick={onClose}
          style={{background:"rgba(255,255,255,.2)",border:"none",cursor:"pointer",
            color:"#fff",borderRadius:"50%",width:"30px",height:"30px",
            display:"flex",alignItems:"center",justifyContent:"center",fontSize:"18px"}}>
          ×
        </button>
        <div style={{flex:1}}>
          <div style={{fontSize:"15px",fontWeight:"600"}}>Check-in Attendees</div>
          <div style={{fontSize:"11px",opacity:.8}}>{congress?.CONGRESSNAME}</div>
        </div>
        <button onClick={load}
          style={{background:"rgba(255,255,255,.2)",border:"none",cursor:"pointer",
            color:"#fff",borderRadius:"6px",padding:"4px 10px",
            fontSize:"12px",fontFamily:"inherit"}}>↻ Refresh</button>
      </div>

      {/* List */}
      <div style={{flex:1,overflowY:"auto"}}>
        {loading&&<div style={{padding:"40px",textAlign:"center",
          color:"#9CA3AF",fontSize:"13px"}}>Loading attendees…</div>}
        {!loading&&attendees.length===0&&(
          <div style={{padding:"60px 20px",textAlign:"center"}}>
            <div style={{fontSize:"32px",marginBottom:"8px"}}>👥</div>
            <div style={{fontSize:"14px",color:"#6B7280"}}>No attendees yet</div>
          </div>
        )}
        {attendees.map(a=>{
          const uid=String(a.USERID);
          const busy=!!acting[uid];
          return(
            <div key={uid} style={{display:"flex",alignItems:"center",gap:"12px",
              padding:"12px 16px",borderBottom:"1px solid #F3F4F6"}}>
              {/* Avatar */}
              <div style={{width:"36px",height:"36px",borderRadius:"50%",
                background:`${color}18`,color,fontSize:"13px",fontWeight:"700",
                display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                {(a.NAME||"?").charAt(0).toUpperCase()}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:"13px",fontWeight:"500",color:"#111",
                  marginBottom:"3px"}}>{a.NAME}</div>
                <StatusBadge status={a.STATUS}/>
              </div>
              {/* Action buttons */}
              <div style={{display:"flex",gap:"6px",flexShrink:0}}>
                {a.STATUS!==1&&(
                  <button onClick={()=>doAction(uid,1)} disabled={busy}
                    title="Approve"
                    style={{padding:"5px 10px",borderRadius:"6px",border:"none",
                      cursor:busy?"not-allowed":"pointer",fontFamily:"inherit",
                      fontSize:"11px",fontWeight:"600",
                      background:"#D1FAE5",color:"#065F46",
                      opacity:busy?0.5:1}}>
                    ✓ Approve
                  </button>
                )}
                {a.STATUS!==0&&(
                  <button onClick={()=>doAction(uid,0)} disabled={busy}
                    title="Deactivate"
                    style={{padding:"5px 10px",borderRadius:"6px",border:"none",
                      cursor:busy?"not-allowed":"pointer",fontFamily:"inherit",
                      fontSize:"11px",fontWeight:"600",
                      background:"#FEF3C7",color:"#92400E",
                      opacity:busy?0.5:1}}>
                    ⏸ Disable
                  </button>
                )}
                <button onClick={()=>doAction(uid,2)} disabled={busy}
                  title="Delete check-in"
                  style={{padding:"5px 10px",borderRadius:"6px",border:"none",
                    cursor:busy?"not-allowed":"pointer",fontFamily:"inherit",
                    fontSize:"11px",fontWeight:"600",
                    background:"#FEE2E2",color:"#991B1B",
                    opacity:busy?0.5:1}}>
                  🗑
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Add Session Panel ────────────────────────────────────────────────────────
function AddSessionPanel({ user, activeSub, congress, sessions, onClose, onAdded }) {
  const ctx   = useContext(ThemeCtx)||{};
  const color = ctx.theme?.primary||C.primary;

  // Extract unique locations and session types (with IDs) from existing sessions
  const locations = [...new Map(
    sessions
      .filter(s=>s.LOCATIONID&&s.LOCATIONNAME)
      .map(s=>[s.LOCATIONID, {id:String(s.LOCATIONID), name:s.LOCATIONNAME}])
  ).values()];

  const sessionTypes = [...new Map(
    sessions
      .filter(s=>s.SESSIONTYPEID&&s.SESSIONTYPENAME)
      .map(s=>[s.SESSIONTYPEID, {id:String(s.SESSIONTYPEID), name:s.SESSIONTYPENAME}])
  ).values()];

  const [form, setForm] = useState({
    title:"", stime:"", etime:"", date:"", type:"", description:"", locationid:""
  });
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");
  const [success, setSuccess] = useState(false);

  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const submit = async() => {
    if(!form.title.trim()||!form.stime||!form.etime){
      setError("Title, Start time and End time are required."); return;
    }
    setError(""); setSaving(true);
    const fd=new FormData();
    fd.append("congressid",  congress.CONGRESSID);
    fd.append("userid",      user.USERID);
    fd.append("title",       form.title.trim());
    fd.append("stime",       form.stime);
    fd.append("etime",       form.etime);
    if(form.date)        fd.append("date",        form.date);
    if(form.type)        fd.append("type",        form.type);
    if(form.description) fd.append("description", form.description.trim());
    if(form.locationid)  fd.append("locationid",  form.locationid.trim());
    try{
      const r=await fetch(`${API}/add_planner`,
        {method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const res=await r.json();
      if(res.STATUS==="1"||res.STATUS===1){
        setSuccess(true);
        setTimeout(()=>{ onAdded&&onAdded(); onClose(); },1500);
      } else {
        setError(res.MESSAGE||"Failed to add session.");
      }
    }catch(e){ setError("Network error. Please try again."); }
    setSaving(false);
  };

  return (
    <div style={{position:"fixed",top:"48px",left:"200px",right:0,bottom:0,
      zIndex:960,display:"flex",flexDirection:"column",background:"#fff"}}>
      {/* Header */}
      <div style={{flexShrink:0,background:color,color:"#fff",
        padding:"14px 20px",display:"flex",alignItems:"center",gap:"12px"}}>
        <button onClick={onClose}
          style={{background:"rgba(255,255,255,.2)",border:"none",cursor:"pointer",
            color:"#fff",borderRadius:"50%",width:"30px",height:"30px",
            display:"flex",alignItems:"center",justifyContent:"center",fontSize:"18px"}}>
          ×
        </button>
        <div style={{flex:1}}>
          <div style={{fontSize:"15px",fontWeight:"600"}}>Add Session</div>
          <div style={{fontSize:"11px",opacity:.8}}>{congress?.CONGRESSNAME}</div>
        </div>
      </div>

      {/* Success */}
      {success&&(
        <div style={{flex:1,display:"flex",flexDirection:"column",
          alignItems:"center",justifyContent:"center",gap:"12px"}}>
          <div style={{fontSize:"48px"}}>✅</div>
          <div style={{fontSize:"16px",fontWeight:"600",color:"#111"}}>Session Added!</div>
          <div style={{fontSize:"13px",color:"#6B7280"}}>Refreshing agenda…</div>
        </div>
      )}

      {/* Form */}
      {!success&&(
        <div style={{flex:1,overflowY:"auto",padding:"20px"}}>
          <Field label="Session Title" required>
            <input value={form.title} onChange={e=>set("title",e.target.value)}
              placeholder="e.g. Zolbetuximab Phase 3 Data"
              style={inputStyle}/>
          </Field>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"12px"}}>
            <Field label="Start Time" required>
              <input type="time" value={form.stime} onChange={e=>set("stime",e.target.value)}
                style={inputStyle}/>
            </Field>
            <Field label="End Time" required>
              <input type="time" value={form.etime} onChange={e=>set("etime",e.target.value)}
                style={inputStyle}/>
            </Field>
          </div>

          <Field label="Date">
            <input type="date" value={form.date} onChange={e=>{
              // Convert YYYY-MM-DD → DD-Mon-YYYY for API
              const d=new Date(e.target.value);
              const months=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
              const fmt=e.target.value?`${String(d.getDate()).padStart(2,"0")}-${months[d.getMonth()]}-${d.getFullYear()}`:"";
              set("date",fmt); e.target.setAttribute("data-raw",e.target.value);
            }}
              style={inputStyle}/>
            {form.date&&<div style={{fontSize:"11px",color:"#6B7280",marginTop:"3px"}}>
              Will be sent as: {form.date}
            </div>}
          </Field>

          <Field label="Session Type">
            <select value={form.type} onChange={e=>set("type",e.target.value)}
              style={{...inputStyle,cursor:"pointer"}}>
              <option value="">— Select type —</option>
              {sessionTypes.map(t=>(
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </Field>

          <Field label="Description">
            <textarea value={form.description} onChange={e=>set("description",e.target.value)}
              placeholder="Session notes or description…" rows={3}
              style={{...inputStyle,resize:"vertical",minHeight:"72px"}}/>
          </Field>

          <Field label="Location">
            <select value={form.locationid} onChange={e=>set("locationid",e.target.value)}
              style={{...inputStyle,cursor:"pointer"}}>
              <option value="">— Select location —</option>
              {locations.map(l=>(
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </Field>

          {error&&(
            <div style={{padding:"10px 14px",borderRadius:"8px",
              background:"#FEF2F2",border:"1px solid #FECACA",
              fontSize:"13px",color:"#B91C1C",marginBottom:"14px"}}>
              {error}
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      {!success&&(
        <div style={{flexShrink:0,padding:"14px 20px",
          borderTop:"1px solid #E5E7EB",
          display:"flex",gap:"10px"}}>
          <button onClick={onClose}
            style={{padding:"10px 20px",borderRadius:"8px",cursor:"pointer",
              border:"1px solid #E5E7EB",background:"#fff",fontFamily:"inherit",
              fontSize:"13px",color:"#374151"}}>
            Cancel
          </button>
          <button onClick={submit} disabled={saving||!form.title.trim()}
            style={{flex:1,padding:"10px",borderRadius:"8px",border:"none",
              cursor:(saving||!form.title.trim())?"not-allowed":"pointer",
              fontFamily:"inherit",fontSize:"13px",fontWeight:"600",
              background:(saving||!form.title.trim())?"#E5E7EB":color,
              color:(saving||!form.title.trim())?"#9CA3AF":"#fff",
              transition:"all .15s"}}>
            {saving?"Adding Session…":"Add Session"}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Admin Bookmark Panel ─────────────────────────────────────────────────────
function AdminBookmarkPanel({ user, activeSub, congress, sessions, onClose }) {
  const ctx   = useContext(ThemeCtx)||{};
  const color = ctx.theme?.primary||C.primary;

  const [step,       setStep]      = useState(1);
  const [filterMode, setFilterMode]= useState("all"); // all | group | congress
  const [users,      setUsers]     = useState([]);
  const [groups,     setGroups]    = useState([]);
  const [selGroup,   setSelGroup]  = useState(null);
  const [bisLoaded,  setBisLoaded] = useState(false);
  const [loading,    setLoading]   = useState(true);
  const [selUsers,   setSelUsers]  = useState([]);
  const [selPres,    setSelPres]   = useState(new Set());
  const [expanded,   setExpanded]  = useState({});
  const [submitting, setSubmitting]= useState(false);
  const [done,       setDone]      = useState(false);
  const [search,     setSearch]    = useState("");
  const groupsRef = useRef([]);

  // Load all users on mount
  const loadUsers = (mode, groupIds) => {
    setLoading(true); setUsers([]);
    const fd=new FormData();
    fd.append("userid",      user.USERID);
    fd.append("subprojectid",activeSub.SUBPROJECTID);
    if(mode==="congress") fd.append("congress","1");
    if(groupIds) groupIds.forEach(id=>fd.append("groupid",String(id)));
    fetch(`${API}/DM_Users`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd})
      .then(r=>r.json())
      .then(res=>{ setUsers(String(res.STATUS)==="1"&&Array.isArray(res.DATA)?res.DATA:[]); setLoading(false); })
      .catch(()=>setLoading(false));
  };

  const fetchBisAndLoad = () => {
    setLoading(true); setUsers([]);
    fetch(`${API}/bis?userid=${user.USERID}&subprojectid=${activeSub.SUBPROJECTID}`,
      {headers:{"Auth-Code":user.AUTH_CODE}})
    .then(r=>r.json())
    .then(res=>{
      const grps=Array.isArray(res.GROUP)?res.GROUP:Array.isArray(res.DATA?.GROUP)?res.DATA.GROUP:[];
      groupsRef.current=grps; setGroups(grps); setBisLoaded(true);
      const primary=grps.find(g=>g.PRIMARY_GRP==="YES")||grps[0];
      setSelGroup(primary);
      if(!grps.length){setLoading(false);return;}
      const fd=new FormData();
      fd.append("userid",user.USERID); fd.append("subprojectid",activeSub.SUBPROJECTID);
      grps.forEach(g=>fd.append("groupid",String(g.GROUPID)));
      fetch(`${API}/DM_Users`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd})
        .then(r=>r.json())
        .then(res2=>{setUsers(String(res2.STATUS)==="1"&&Array.isArray(res2.DATA)?res2.DATA:[]); setLoading(false);})
        .catch(()=>setLoading(false));
    }).catch(()=>setLoading(false));
  };

  const selectGroup = g => {
    setSelGroup(g); setLoading(true); setUsers([]);
    const fd=new FormData();
    fd.append("userid",user.USERID); fd.append("subprojectid",activeSub.SUBPROJECTID);
    fd.append("groupid",String(g.GROUPID));
    fetch(`${API}/DM_Users`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd})
      .then(r=>r.json())
      .then(res=>{setUsers(String(res.STATUS)==="1"&&Array.isArray(res.DATA)?res.DATA:[]); setLoading(false);})
      .catch(()=>setLoading(false));
  };

  const switchFilter = mode => {
    setFilterMode(mode); setSearch("");
    if(mode==="all")      { loadUsers("all",null); return; }
    if(mode==="congress") { loadUsers("congress",null); return; }
    if(mode==="group")    { fetchBisAndLoad(); }
  };

  useEffect(()=>{ loadUsers("all",null); },[]);

  const filteredUsers = users.filter(u=>
    !search||(u.NAME||u.FIRSTNAME||"").toLowerCase().includes(search.toLowerCase())
  );

  const toggleUser = uid => setSelUsers(prev=>
    prev.includes(uid)?prev.filter(x=>x!==uid):[...prev,uid]
  );

  const toggleSession = s => {
    const ids=s.SUBSESSIONS?.map(ss=>ss.PRESENTATIONID)||[];
    setSelPres(prev=>{
      const next=new Set(prev);
      const allIn=ids.every(id=>next.has(id));
      ids.forEach(id=>allIn?next.delete(id):next.add(id));
      return next;
    });
  };

  const toggleSub = pid => setSelPres(prev=>{
    const next=new Set(prev); next.has(pid)?next.delete(pid):next.add(pid); return next;
  });

  const sessionChecked = s => !!s.SUBSESSIONS?.length&&s.SUBSESSIONS.every(ss=>selPres.has(ss.PRESENTATIONID));
  const sessionPartial = s => s.SUBSESSIONS?.some(ss=>selPres.has(ss.PRESENTATIONID))&&!sessionChecked(s);

  const submit = async() => {
    if(!selUsers.length||!selPres.size) return;
    setSubmitting(true);
    const fd=new FormData();
    fd.append("congressid",congress.CONGRESSID);
    selUsers.forEach(uid=>fd.append("userid[]",uid));
    [...selPres].forEach(pid=>fd.append("presentationid[]",pid));
    try{
      await fetch(`${API}/admin_bookmark`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      setDone(true);
    }catch(e){}
    setSubmitting(false);
  };

  const Checkbox = ({checked,partial,onChange})=>(
    <div onClick={e=>{e.stopPropagation();onChange();}}
      style={{width:"18px",height:"18px",borderRadius:"4px",flexShrink:0,cursor:"pointer",
        border:`2px solid ${checked||partial?color:"#D1D5DB"}`,
        background:checked?color:partial?`${color}22`:"#fff",
        display:"flex",alignItems:"center",justifyContent:"center"}}>
      {checked&&<svg width="10" height="10" viewBox="0 0 12 12" fill="none">
        <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
      </svg>}
      {partial&&!checked&&<div style={{width:"8px",height:"2px",background:color,borderRadius:"1px"}}/>}
    </div>
  );

  return (
    <div style={{position:"fixed",top:"48px",left:"200px",right:0,bottom:0,
      zIndex:960,display:"flex",flexDirection:"column",background:"#fff"}}>
      {/* Header */}
      <div style={{flexShrink:0,background:color,color:"#fff",
        padding:"14px 20px",display:"flex",alignItems:"center",gap:"12px"}}>
        <button onClick={onClose}
          style={{background:"rgba(255,255,255,.2)",border:"none",cursor:"pointer",
            color:"#fff",borderRadius:"50%",width:"30px",height:"30px",
            display:"flex",alignItems:"center",justifyContent:"center",fontSize:"18px"}}>
          ×
        </button>
        <div style={{flex:1}}>
          <div style={{fontSize:"15px",fontWeight:"600"}}>Admin Bookmark</div>
          <div style={{fontSize:"11px",opacity:.8}}>{congress?.CONGRESSNAME}</div>
        </div>
        {(selUsers.length>0||selPres.size>0)&&(
          <div style={{fontSize:"11px",opacity:.85,textAlign:"right"}}>
            {selUsers.length} user{selUsers.length!==1?"s":""}<br/>
            {selPres.size} presentation{selPres.size!==1?"s":""}
          </div>
        )}
      </div>

      {/* Step tabs */}
      <div style={{flexShrink:0,display:"flex",borderBottom:"1px solid #E5E7EB",background:"#fff"}}>
        {[{n:1,l:"1. Select Users"},{n:2,l:"2. Select Sessions"}].map(t=>(
          <button key={t.n} onClick={()=>setStep(t.n)}
            style={{flex:1,padding:"10px",border:"none",cursor:"pointer",background:"none",
              fontFamily:"inherit",fontSize:"12px",fontWeight:step===t.n?"600":"400",
              color:step===t.n?color:"#6B7280",
              borderBottom:`2px solid ${step===t.n?color:"transparent"}`,
              display:"flex",alignItems:"center",justifyContent:"center",gap:"6px"}}>
            {t.l}
            {t.n===1&&selUsers.length>0&&<span style={{fontSize:"10px",fontWeight:"700",
              background:color,color:"#fff",padding:"1px 6px",borderRadius:"20px"}}>
              {selUsers.length}</span>}
            {t.n===2&&selPres.size>0&&<span style={{fontSize:"10px",fontWeight:"700",
              background:color,color:"#fff",padding:"1px 6px",borderRadius:"20px"}}>
              {selPres.size}</span>}
          </button>
        ))}
      </div>

      {/* Done */}
      {done&&(
        <div style={{flex:1,display:"flex",flexDirection:"column",
          alignItems:"center",justifyContent:"center",gap:"12px",padding:"40px"}}>
          <div style={{fontSize:"48px"}}>✅</div>
          <div style={{fontSize:"16px",fontWeight:"600",color:"#111"}}>Bookmarks Assigned!</div>
          <div style={{fontSize:"13px",color:"#6B7280",textAlign:"center"}}>
            {selPres.size} presentations assigned to {selUsers.length} users. Push notifications sent.
          </div>
          <button onClick={onClose}
            style={{marginTop:"12px",padding:"10px 28px",borderRadius:"8px",
              border:"none",background:color,color:"#fff",cursor:"pointer",
              fontFamily:"inherit",fontSize:"13px",fontWeight:"600"}}>Done</button>
        </div>
      )}

      {/* Step 1 — Users */}
      {!done&&step===1&&(
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
          {/* 3 filter tabs */}
          <div style={{flexShrink:0,display:"flex",gap:"6px",padding:"10px 14px",
            borderBottom:"1px solid #F3F4F6",background:"#fff"}}>
            {[{id:"all",l:"All Users"},{id:"group",l:"By Group"},{id:"congress",l:"Congress"}].map(f=>(
              <button key={f.id} onClick={()=>switchFilter(f.id)}
                style={{padding:"4px 12px",borderRadius:"20px",cursor:"pointer",
                  fontSize:"11px",fontWeight:"500",fontFamily:"inherit",
                  border:`1px solid ${filterMode===f.id?color:"#E5E7EB"}`,
                  background:filterMode===f.id?color:"#fff",
                  color:filterMode===f.id?"#fff":"#6B7280",transition:"all .15s"}}>
                {f.l}
              </button>
            ))}
          </div>
          {/* Group pills */}
          {filterMode==="group"&&bisLoaded&&groups.length>0&&(
            <div style={{flexShrink:0,display:"flex",gap:"6px",flexWrap:"wrap",
              padding:"8px 14px",borderBottom:"1px solid #F3F4F6",background:"#F8FAFC"}}>
              {groups.map(g=>(
                <button key={g.GROUPID} onClick={()=>selectGroup(g)}
                  style={{padding:"3px 10px",borderRadius:"20px",cursor:"pointer",
                    fontSize:"11px",fontWeight:"500",fontFamily:"inherit",
                    border:`1px solid ${selGroup?.GROUPID===g.GROUPID?color:"#E5E7EB"}`,
                    background:selGroup?.GROUPID===g.GROUPID?color:"#fff",
                    color:selGroup?.GROUPID===g.GROUPID?"#fff":"#6B7280"}}>
                  {g.GROUPNAME}
                </button>
              ))}
            </div>
          )}
          {/* Search */}
          <div style={{flexShrink:0,padding:"8px 14px",borderBottom:"1px solid #F3F4F6"}}>
            <div style={{display:"flex",alignItems:"center",gap:"8px",
              background:"#F3F4F6",borderRadius:"8px",padding:"6px 10px"}}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                stroke="#9CA3AF" strokeWidth="2.5">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
              <input value={search} onChange={e=>setSearch(e.target.value)}
                placeholder="Search users…"
                style={{flex:1,border:"none",background:"transparent",
                  fontSize:"12px",outline:"none",fontFamily:"inherit"}}/>
              {search&&<button onClick={()=>setSearch("")}
                style={{background:"none",border:"none",cursor:"pointer",
                  color:"#9CA3AF",fontSize:"14px",padding:0}}>×</button>}
            </div>
          </div>
          {/* User list */}
          <div style={{flex:1,overflowY:"auto"}}>
            {loading&&<div style={{padding:"30px",textAlign:"center",
              color:"#9CA3AF",fontSize:"12px"}}>Loading…</div>}
            {!loading&&filteredUsers.length===0&&(
              <div style={{padding:"30px",textAlign:"center",
                color:"#9CA3AF",fontSize:"12px"}}>No users found</div>
            )}
            {filteredUsers.map(u=>{
              const uid=String(u.USERID||u.DMUSER_ID||"");
              const name=u.NAME||(u.FIRSTNAME&&u.LASTNAME?`${u.FIRSTNAME} ${u.LASTNAME}`.trim():u.FIRSTNAME||"");
              const checked=selUsers.includes(uid);
              return(
                <div key={uid} onClick={()=>toggleUser(uid)}
                  style={{display:"flex",alignItems:"center",gap:"12px",
                    padding:"10px 16px",borderBottom:"1px solid #F3F4F6",
                    cursor:"pointer",background:checked?`${color}06`:"#fff",
                    transition:"background .1s"}}>
                  <Checkbox checked={checked} partial={false} onChange={()=>toggleUser(uid)}/>
                  <div style={{width:"32px",height:"32px",borderRadius:"50%",
                    background:`${color}18`,color,fontSize:"12px",fontWeight:"700",
                    display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    {(name||"?").charAt(0).toUpperCase()}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:"13px",fontWeight:"500",color:"#111"}}>{name}</div>
                  </div>
                  {checked&&<svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke={color} strokeWidth="2.5">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>}
                </div>
              );
            })}
          </div>
          {/* Next button */}
          <div style={{flexShrink:0,padding:"12px 16px",borderTop:"1px solid #E5E7EB"}}>
            <button onClick={()=>setStep(2)} disabled={!selUsers.length}
              style={{width:"100%",padding:"11px",borderRadius:"8px",border:"none",
                cursor:selUsers.length?"pointer":"not-allowed",fontFamily:"inherit",
                fontSize:"13px",fontWeight:"600",
                background:selUsers.length?color:"#E5E7EB",
                color:selUsers.length?"#fff":"#9CA3AF",transition:"all .15s"}}>
              Next: Select Sessions ({selUsers.length} selected) →
            </button>
          </div>
        </div>
      )}

      {/* Step 2 — Sessions */}
      {!done&&step===2&&(
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
          <div style={{flex:1,overflowY:"auto",padding:"10px 14px"}}>
            {sessions.map(s=>{
              const hasSubs=s.SUBSESSIONS?.length>0;
              const checked=sessionChecked(s);
              const partial=sessionPartial(s);
              const isExp=!!expanded[s.DOCUMENTID];
              return(
                <div key={s.DOCUMENTID} style={{marginBottom:"6px",
                  border:"1px solid #E5E7EB",borderRadius:"8px",overflow:"hidden"}}>
                  <div style={{display:"flex",alignItems:"center",gap:"10px",
                    padding:"10px 12px",
                    background:checked?`${color}06`:partial?`${color}03`:"#fff",
                    cursor:"pointer"}}
                    onClick={()=>hasSubs&&setExpanded(e=>({...e,[s.DOCUMENTID]:!e[s.DOCUMENTID]}))}>
                    <Checkbox checked={checked} partial={partial} onChange={()=>toggleSession(s)}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:"12px",fontWeight:"600",color:"#111",
                        lineHeight:1.3,marginBottom:"2px"}}>{s.TITLE}</div>
                      <div style={{fontSize:"10px",color:"#9CA3AF"}}>
                        {s.SESSIONTYPENAME}{s.SUBSESSIONS?.length?` · ${s.SUBSESSIONS.length} presentations`:""}
                      </div>
                    </div>
                    {hasSubs&&(
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                        stroke="#9CA3AF" strokeWidth="2.5"
                        style={{transition:"transform .2s",transform:isExp?"rotate(180deg)":"none"}}>
                        <path d="M6 9l6 6 6-6"/>
                      </svg>
                    )}
                  </div>
                  {isExp&&hasSubs&&s.SUBSESSIONS.map(ss=>(
                    <div key={ss.PRESENTATIONID}
                      style={{display:"flex",alignItems:"center",gap:"10px",
                        padding:"8px 12px 8px 40px",
                        borderTop:"1px solid #F3F4F6",
                        background:selPres.has(ss.PRESENTATIONID)?`${color}06`:"#FAFAFA",
                        cursor:"pointer"}}
                      onClick={()=>toggleSub(ss.PRESENTATIONID)}>
                      <Checkbox checked={selPres.has(ss.PRESENTATIONID)}
                        partial={false} onChange={()=>toggleSub(ss.PRESENTATIONID)}/>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:"11px",fontWeight:"500",color:"#374151",
                          lineHeight:1.3}}>{ss.SUBSESSIONTITLE}</div>
                        {ss.STARTTIME&&<div style={{fontSize:"10px",color:"#9CA3AF"}}>
                          {ss.STARTTIME}{ss.ENDTIME?` – ${ss.ENDTIME}`:""}
                        </div>}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
          <div style={{flexShrink:0,padding:"12px 16px",borderTop:"1px solid #E5E7EB",
            display:"flex",gap:"8px"}}>
            <button onClick={()=>setStep(1)}
              style={{padding:"11px 20px",borderRadius:"8px",
                border:"1px solid #E5E7EB",background:"#fff",cursor:"pointer",
                fontFamily:"inherit",fontSize:"13px",color:"#374151"}}>
              ← Back
            </button>
            <button onClick={submit}
              disabled={!selUsers.length||!selPres.size||submitting}
              style={{flex:1,padding:"11px",borderRadius:"8px",border:"none",
                cursor:(selUsers.length&&selPres.size&&!submitting)?"pointer":"not-allowed",
                fontFamily:"inherit",fontSize:"13px",fontWeight:"600",
                background:(selUsers.length&&selPres.size&&!submitting)?color:"#E5E7EB",
                color:(selUsers.length&&selPres.size&&!submitting)?"#fff":"#9CA3AF"}}>
              {submitting?"Assigning…":`Assign to ${selUsers.length} user${selUsers.length!==1?"s":""}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Planner ─────────────────────────────────────────────────────────────
export default function CongressPlanner({ user, subproject:activeSub,
  congress:propCongress, onBack,
  initPresentationId, initDocId, initDoctypeId, initCommentId }) {
  const ctx   = useContext(ThemeCtx)||{};
  const color = ctx.theme?.primary||C.primary;

  const params        = new URLSearchParams(window.location.search);
  const urlCongressId = params.get("congress");
  const congressId    = propCongress?.CONGRESSID||urlCongressId;

  const [congress,    setCongress]   = useState(propCongress||null);
  const [sessions,    setSessions]   = useState([]);
  const [loading,     setLoading]    = useState(true);
  const [expanded,    setExpanded]   = useState({});
  const [selSession,  setSelSession] = useState(null);
  const [filterType,  setFilterType] = useState("all");
  const [filterDate,  setFilterDate] = useState("all");
  const [filterSType, setFilterSType]= useState("all");
  const [search,      setSearch]     = useState("");
  const [showAdminBM,    setShowAdminBM]   = useState(false);
  const [showAddSession,  setShowAddSession]  = useState(false);
  const [showAttendees,   setShowAttendees]   = useState(false);
  const [checkInStatus,   setCheckInStatus]   = useState(congress?.CHECKIN_STATUS); // null|0|1
  const [checkingIn,      setCheckingIn]      = useState(false);

  // Sync checkInStatus when congress loads
  useEffect(()=>{ setCheckInStatus(congress?.CHECKIN_STATUS); },[congress?.CONGRESSID]);

  const doCheckIn = async() => {
    if(checkingIn) return;
    setCheckingIn(true);
    const fd=new FormData();
    fd.append("userid",    user.USERID);
    fd.append("congressid",congressId);
    try{
      const r=await fetch(`${API}/check_in`,
        {method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const res=await r.json();
      if(res.STATUS==="1"||res.STATUS===1){
        const newStatus=res.DATA?.[0]?.CHECKINSTATUS??0;
        setCheckInStatus(newStatus);
      }
    }catch(e){}
    setCheckingIn(false);
  };

  const isCongressAdmin = activeSub?.CONGRESS_ADMIN===1||activeSub?.CONGRESS_ADMIN==="1";
  const isExportUser   = activeSub?.EXPORT===1||activeSub?.EXPORT==="1";
  const [exporting,    setExporting]   = useState(false);
  const [exportDone,   setExportDone]  = useState(false);

  const doExport = async() => {
    if(exporting||!congress?.CONGRESSID) return;
    setExporting(true); setExportDone(false);
    const fd=new FormData();
    fd.append("userid",    user.USERID);
    fd.append("congressid",congress.CONGRESSID);
    try{
      const r=await fetch(`${API}/export_congress`,
        {method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const res=await r.json();
      if(res.STATUS==="1"||res.STATUS===1) setExportDone(true);
    }catch(e){}
    setExporting(false);
  };
  useEffect(()=>{
    if(propCongress){setCongress(propCongress);return;}
    if(!urlCongressId||!user||!activeSub) return;
    (async()=>{
      try{
        const r=await fetch(
          `${API}/select_conference?userid=${user.USERID}&subprojectid=${activeSub.SUBPROJECTID}`,
          {headers:{"Auth-Code":user.AUTH_CODE}});
        const res=await r.json();
        if(String(res.STATUS)==="1"&&Array.isArray(res.DATA))
          setCongress(res.DATA.find(x=>String(x.CONGRESSID)===String(urlCongressId))||null);
      }catch(e){}
    })();
  },[urlCongressId,activeSub?.SUBPROJECTID]);

  useEffect(()=>{
    if(!congressId||!user) return;
    setLoading(true);
    (async()=>{
      try{
        const r=await fetch(`${API}/planner?congressid=${congressId}&userid=${user.USERID}`,
          {headers:{"Auth-Code":user.AUTH_CODE}});
        const res=await r.json();
        setSessions(String(res.STATUS)==="1"&&Array.isArray(res.DATA)?res.DATA:[]);
      }catch(e){setSessions([]);}
      setLoading(false);
    })();
  },[congressId]);

  // auto-open presentation from URL params once sessions load
  useEffect(()=>{
    if(!initPresentationId || loading || sessions.length===0 || selSession) return;
    // find session that contains this presentationid in its SUBSESSIONS
    const match = sessions.find(s=>
      s.SUBSESSIONS?.some(ss=>String(ss.PRESENTATIONID)===String(initPresentationId))
    );
    if(match) {
      setExpanded(e=>({...e,[match.DOCUMENTID]:true}));
      const sub = match.SUBSESSIONS.find(ss=>String(ss.PRESENTATIONID)===String(initPresentationId));
      if(sub) setSelSession({
        ...sub,
        PARENT_DOCUMENTID: match.DOCUMENTID,
        PARENTTITLE: match.TITLE,
      });
    }
  },[loading, sessions]);

  if(showAttendees) return(
    <AttendeePanel user={user} activeSub={activeSub} congress={congress}
      onClose={()=>setShowAttendees(false)}/>
  );

  if(showAddSession) return(
    <AddSessionPanel
      user={user} activeSub={activeSub} congress={congress}
      sessions={sessions}
      onClose={()=>setShowAddSession(false)}
      onAdded={()=>{
        // Reload sessions after adding
        setLoading(true);
        fetch(`${API}/planner?congressid=${congressId}&userid=${user.USERID}`,
          {headers:{"Auth-Code":user.AUTH_CODE}})
          .then(r=>r.json())
          .then(res=>{ setSessions(String(res.STATUS)==="1"&&Array.isArray(res.DATA)?res.DATA:[]); setLoading(false); })
          .catch(()=>setLoading(false));
      }}/>
  );

  if(showAdminBM) return(
    <AdminBookmarkPanel
      user={user} activeSub={activeSub} congress={congress}
      sessions={sessions} onClose={()=>setShowAdminBM(false)}/>
  );

  if(selSession) return(
    <div style={{position:"fixed",top:"48px",left:"200px",right:0,bottom:0,
      zIndex:950,background:"#fff",display:"flex",flexDirection:"column",overflow:"hidden"}}>
      <PresentationDetail sub={selSession} congress={congress}
        user={user} activeSub={activeSub} color={color}
        onBack={()=>setSelSession(null)}
        initCommentId={initCommentId}/>
    </div>
  );

  const toggle = id=>setExpanded(e=>({...e,[id]:!e[id]}));

  const allDates  = [...new Set(sessions.map(s=>s.DOCDATE).filter(Boolean))].sort();
  const allSTypes = [...new Set(sessions.map(s=>s.SESSIONTYPENAME).filter(Boolean))].sort();

  const filtered = sessions.filter(s=>{
    if(filterType==="bookmarked"&&!(s.BOOKMARKED===1||s.BOOKMARKED==="1")) return false;
    if(filterType==="hot"&&!(s.HOTTOPIC===1||s.HOTTOPIC==="1")) return false;
    if(filterDate!=="all"&&s.DOCDATE!==filterDate) return false;
    if(filterSType!=="all"&&s.SESSIONTYPENAME!==filterSType) return false;
    if(search&&!s.TITLE?.toLowerCase().includes(search.toLowerCase())&&
       !s.LOCATIONNAME?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const grouped = filtered.reduce((acc,s)=>{
    const d=s.DOCDATE||"Undated";
    if(!acc[d]) acc[d]=[];
    acc[d].push(s);
    return acc;
  },{});

  return(
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden",background:"#F8FAFC"}}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      {/* Hero */}
      <div style={{flexShrink:0,background:color,color:"#fff",padding:"12px 20px 10px"}}>
        <button onClick={()=>onBack?onBack():window.history.back()}
          style={{display:"inline-flex",alignItems:"center",gap:"5px",
            fontSize:"12px",opacity:.85,background:"none",border:"none",
            cursor:"pointer",color:"#fff",fontFamily:"inherit",
            padding:0,marginBottom:"8px"}}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back
        </button>
        {congress&&(
          <div style={{display:"flex",alignItems:"center",
            justifyContent:"space-between",gap:"12px"}}>
            <div style={{minWidth:0}}>
              <div style={{fontSize:"17px",fontWeight:"600",letterSpacing:"-.2px",
                lineHeight:1.2,overflow:"hidden",textOverflow:"ellipsis",
                whiteSpace:"nowrap"}}>{congress.CONGRESSNAME}</div>
            </div>
            <div style={{flexShrink:0,textAlign:"right",fontSize:"11px",opacity:.8}}>
              <div style={{marginBottom:"2px"}}>📅 {fmtDateRange(congress)}</div>
              {congress.CITY&&<div>📍 {congress.CITY}, {congress.COUNTRYNAME}</div>}
            </div>
          </div>
        )}
      </div>

      {/* Action buttons bar */}
      <div style={{flexShrink:0,display:"flex",gap:"8px",
        padding:"8px 16px",background:"#fff",
        borderBottom:"1px solid #E5E7EB",alignItems:"center"}}>
        {isCongressAdmin&&(
          <button onClick={()=>setShowAddSession(true)}
            style={{display:"flex",alignItems:"center",gap:"5px",
              padding:"6px 12px",borderRadius:"6px",
              border:`1px solid ${color}`,background:`${color}10`,
              cursor:"pointer",fontFamily:"inherit",
              fontSize:"12px",fontWeight:"600",color,
              transition:"all .15s"}}
            onMouseEnter={e=>{e.currentTarget.style.background=color;e.currentTarget.style.color="#fff";}}
            onMouseLeave={e=>{e.currentTarget.style.background=`${color}10`;e.currentTarget.style.color=color;}}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Add Session
          </button>
        )}
        {/* Check-in button — only for active congress */}
        {String(congress?.CONGRESS_STATUS)==="1"&&(
          checkInStatus===1
            ? <span style={{display:"flex",alignItems:"center",gap:"5px",
                padding:"6px 12px",borderRadius:"6px",fontSize:"12px",
                fontWeight:"600",color:"#065F46",background:"#D1FAE5",
                border:"1px solid #A7F3D0"}}>
                ✓ Checked In
              </span>
            : checkInStatus===0
              ? <span style={{display:"flex",alignItems:"center",gap:"5px",
                  padding:"6px 12px",borderRadius:"6px",fontSize:"12px",
                  fontWeight:"600",color:"#92400E",background:"#FEF3C7",
                  border:"1px solid #FDE68A"}}>
                  ⏳ Pending Approval
                </span>
              : <button onClick={doCheckIn} disabled={checkingIn}
                  style={{display:"flex",alignItems:"center",gap:"5px",
                    padding:"6px 12px",borderRadius:"6px",border:"1px solid #E5E7EB",
                    background:"#fff",cursor:checkingIn?"not-allowed":"pointer",
                    fontFamily:"inherit",fontSize:"12px",fontWeight:"600",
                    color:"#374151",transition:"all .15s"}}>
                  {checkingIn?"Checking in…":"📍 Check In"}
                </button>
        )}
        {/* Attendees button — admin only */}
        {isCongressAdmin&&(
          <button onClick={()=>setShowAttendees(true)}
            style={{display:"flex",alignItems:"center",gap:"5px",
              padding:"6px 12px",borderRadius:"6px",border:"1px solid #E5E7EB",
              background:"#fff",cursor:"pointer",fontFamily:"inherit",
              fontSize:"12px",fontWeight:"500",color:"#374151",
              transition:"all .15s"}}
            onMouseEnter={e=>e.currentTarget.style.background="#F3F4F6"}
            onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
            👥 Attendees
          </button>
        )}
        {isExportUser&&(
          <button onClick={doExport} disabled={exporting}
            title={exportDone?"Export queued — check your email!":"Export congress as PPTX"}
            style={{display:"flex",alignItems:"center",gap:"5px",
              padding:"6px 12px",borderRadius:"6px",
              border:`1px solid ${exportDone?"#059669":exporting?"#E5E7EB":"#E5E7EB"}`,
              background:exportDone?"#D1FAE5":exporting?"#F9FAFB":"#fff",
              cursor:exporting?"not-allowed":"pointer",fontFamily:"inherit",
              fontSize:"12px",fontWeight:"500",
              color:exportDone?"#065F46":exporting?"#9CA3AF":"#374151",
              transition:"all .2s"}}>
            {exportDone
              ? <><svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>Check your email</>
              : exporting
                ? <><svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2"
                    style={{animation:"spin 1s linear infinite"}}>
                    <path d="M21 12a9 9 0 11-6.219-8.56"/>
                  </svg>Exporting…</>
                : <><svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>Export</>
            }
          </button>
        )}

        {isCongressAdmin&&(
          <button onClick={()=>setShowAdminBM(true)}
            style={{display:"flex",alignItems:"center",gap:"5px",
              padding:"6px 12px",borderRadius:"6px",
              border:`1px solid ${color}`,
              background:`${color}10`,cursor:"pointer",fontFamily:"inherit",
              fontSize:"12px",fontWeight:"600",color,marginLeft:"auto",
              transition:"all .15s"}}
            onMouseEnter={e=>{e.currentTarget.style.background=color;e.currentTarget.style.color="#fff";}}
            onMouseLeave={e=>{e.currentTarget.style.background=`${color}10`;e.currentTarget.style.color=color;}}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2">
              <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/>
            </svg>
            Admin Bookmark
          </button>
        )}
      </div>

      {/* Main body — 2 columns */}
      <div style={{flex:1,overflow:"hidden",display:"flex",gap:0}}>

        {/* Left — agenda */}
        <div style={{flex:1,minWidth:0,display:"flex",flexDirection:"column",overflow:"hidden"}}>

          {/* Filter bar */}
          <div style={{flexShrink:0,padding:"10px 14px",background:"#fff",
            borderBottom:"1px solid #E5E7EB",display:"flex",gap:"8px",
            alignItems:"center",flexWrap:"wrap"}}>
            <div style={{flex:1,minWidth:"120px",display:"flex",alignItems:"center",
              gap:"8px",background:"#F3F4F6",border:"1px solid #E5E7EB",
              borderRadius:"8px",padding:"6px 10px"}}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                stroke="#9CA3AF" strokeWidth="2.5">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
              <input value={search} onChange={e=>setSearch(e.target.value)}
                placeholder="Search sessions…"
                style={{flex:1,border:"none",background:"transparent",
                  fontSize:"12px",outline:"none",fontFamily:"inherit",color:"#111"}}/>
              {search&&<button onClick={()=>setSearch("")}
                style={{background:"none",border:"none",cursor:"pointer",
                  color:"#9CA3AF",fontSize:"14px",lineHeight:1,padding:0}}>×</button>}
            </div>
            <div style={{display:"flex",gap:"4px",background:"#F3F4F6",
              padding:"3px",borderRadius:"8px",border:"1px solid #E5E7EB"}}>
              {[{id:"all",l:"All"},{id:"hot",l:"🔥"},{id:"bookmarked",l:"🔖"}].map(f=>(
                <button key={f.id} onClick={()=>setFilterType(f.id)}
                  style={{padding:"4px 10px",borderRadius:"6px",border:"none",
                    cursor:"pointer",fontSize:"12px",fontWeight:"500",
                    background:filterType===f.id?color:"transparent",
                    color:filterType===f.id?"#fff":"#6B7280",
                    fontFamily:"inherit",transition:"all .15s",title:f.l}}>
                  {f.l}
                </button>
              ))}
            </div>
          </div>

          {/* Date filter — draggable */}
          {!loading&&allDates.length>1&&(
            <DateFilterRow
              dates={allDates}
              active={filterDate}
              onSelect={setFilterDate}
              color={color}/>
          )}

          {/* Session type filter — draggable scroll like Clinical Trials sponsor */}
          {!loading&&allSTypes.length>0&&(
            <TypeFilterRow
              types={allSTypes}
              active={filterSType}
              onSelect={setFilterSType}
              color={color}/>
          )}

          {/* Sessions */}
          <div style={{flex:1,overflowY:"auto",padding:"14px"}}>
            {loading&&<div style={{textAlign:"center",padding:"60px",
              color:"#9CA3AF",fontSize:"13px"}}>Loading agenda…</div>}
            {!loading&&filtered.length===0&&(
              <div style={{textAlign:"center",padding:"60px",
                color:"#9CA3AF",fontSize:"13px"}}>No sessions found</div>
            )}
            {Object.entries(grouped).map(([day,daySessions])=>{
              const handleBookmark=id=>setSessions(prev=>prev.map(x=>{
                if(x.DOCUMENTID===id){
                  const v=x.BOOKMARKED===1?0:1;
                  return {...x,BOOKMARKED:v,SUBSESSIONS:x.SUBSESSIONS?.map(ss=>({...ss,BOOKMARKED:v}))};
                }
                const hasSub=x.SUBSESSIONS?.some(ss=>ss.PRESENTATIONID===id);
                if(hasSub){
                  const subs=x.SUBSESSIONS.map(ss=>ss.PRESENTATIONID===id?{...ss,BOOKMARKED:ss.BOOKMARKED===1?0:1}:ss);
                  return {...x,BOOKMARKED:subs.some(ss=>ss.BOOKMARKED===1)?1:0,SUBSESSIONS:subs};
                }
                return x;
              }));
              return(
                <div key={day} style={{marginBottom:"24px"}}>
                  <div style={{display:"flex",alignItems:"center",
                    gap:"10px",marginBottom:"10px"}}>
                    <div style={{flex:1,height:"1px",background:"#E5E7EB"}}/>
                    <div style={{fontSize:"11px",fontWeight:"700",color:"#6B7280",
                      textTransform:"uppercase",letterSpacing:".6px",
                      background:"#F8FAFC",padding:"0 8px",whiteSpace:"nowrap"}}>
                      {day}
                    </div>
                    <div style={{flex:1,height:"1px",background:"#E5E7EB"}}/>
                  </div>
                  {daySessions.map(s=>(
                    <SessionTile key={s.DOCUMENTID} session={s} color={color}
                      expanded={!!expanded[s.DOCUMENTID]}
                      onToggle={()=>toggle(s.DOCUMENTID)}
                      onSelect={setSelSession}
                      user={user} activeSub={activeSub} congress={congress}
                      onBookmarkToggle={handleBookmark}/>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right — uploads + reports */}
        {congress&&(
          <div style={{width:"280px",flexShrink:0,overflow:"hidden"}}>
            <CongressDocs congress={congress} user={user} activeSub={activeSub}/>
          </div>
        )}
      </div>
    </div>
  );
}